import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtTons, fmtDate, monthLabel, monthShort, fmtMoney } from "@/lib/format";
import { CalendarDays, CalendarRange, TrendingUp, BarChart3, PieChart as PieIcon, Scale } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const PIE_COLORS = ["#0EA5E9", "#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#14B8A6", "#EC4899"];

function TonsCard({ title, subtitle, data, icon: Icon, accent, amount }) {
  const s1 = data?.by_plant?.["SFX 1"]?.tons || 0;
  const s2 = data?.by_plant?.["SFX 2"]?.tons || 0;
  return (
    <div className="relative bg-white border border-slate-200 rounded-lg p-6 shadow-sm overflow-hidden transition-transform duration-200 hover:-translate-y-0.5" data-testid={`dash-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`absolute top-0 left-0 right-0 h-1 ${accent}`} />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="p-2 rounded-md bg-slate-100 text-slate-600"><Icon className="h-4 w-4" /></span>
          <h3 className="font-chivo font-bold text-slate-900">{title}</h3>
        </div>
        <span className="text-xs text-slate-400 font-medium">{subtitle}</span>
      </div>
      <div className="mt-5 flex items-end justify-between gap-3">
        <div>
          <div className="text-5xl font-chivo font-black text-slate-900 tabular leading-none">{fmtTons(data?.total_tons)}</div>
          <div className="text-xs text-slate-400 uppercase tracking-wider mt-2">Total tons · {data?.count || 0} txns</div>
        </div>
        {amount != null && (
          <div className="text-right" data-testid={`dash-amount-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-2xl font-chivo font-black text-emerald-600 tabular leading-none">{fmtMoney(amount)}</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider mt-2">Total sales</div>
          </div>
        )}
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-md bg-sky-50 border border-sky-100 px-3 py-2.5">
          <div className="text-xs text-sky-700 font-bold uppercase tracking-wide">SFX 1</div>
          <div className="font-chivo font-black text-xl text-sky-800 tabular mt-0.5">{fmtTons(s1)} <span className="text-sm font-medium">t</span></div>
        </div>
        <div className="rounded-md bg-indigo-50 border border-indigo-100 px-3 py-2.5">
          <div className="text-xs text-indigo-700 font-bold uppercase tracking-wide">SFX 2</div>
          <div className="font-chivo font-black text-xl text-indigo-800 tabular mt-0.5">{fmtTons(s2)} <span className="text-sm font-medium">t</span></div>
        </div>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-lg px-3 py-2 text-sm">
      <div className="font-semibold text-slate-900 mb-1">{monthLabel(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />
          <span className="text-slate-600">{p.name}:</span>
          <span className="font-semibold tabular">{fmtTons(p.value)} t</span>
        </div>
      ))}
    </div>
  );
}

function CompareBox({ label, valueLabel, tons, amount }) {
  return (
    <div className="mt-4 grid grid-cols-2 gap-3">
      <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-3">
        <div className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1"><Scale className="h-3.5 w-3.5" /> Total Tons</div>
        <div className="font-chivo font-black text-2xl text-slate-900 tabular mt-1">{fmtTons(tons)} <span className="text-base font-medium">t</span></div>
      </div>
      <div className="rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3">
        <div className="text-xs text-emerald-700 uppercase tracking-wide">Total Sales</div>
        <div className="font-chivo font-black text-2xl text-emerald-700 tabular mt-1">{fmtMoney(amount)}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [months, setMonths] = useState([]);
  const [fys, setFys] = useState([]);
  const [selMonth, setSelMonth] = useState("");
  const [selFy, setSelFy] = useState("");
  const [monthStat, setMonthStat] = useState(null);
  const [fyStat, setFyStat] = useState(null);
  const [shares, setShares] = useState([]);

  useEffect(() => {
    api.get("/reports/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/reports/trends", { params: { months: 12 } }).then((r) => setTrend(r.data)).catch(() => {});
    api.get("/reports/customer-share").then((r) => setShares(r.data.shares)).catch(() => {});
    api.get("/reports/months").then((r) => {
      setMonths(r.data);
      if (r.data.length) setSelMonth(r.data[0]);
    }).catch(() => {});
    api.get("/reports/financial-years").then((r) => {
      setFys(r.data);
      if (r.data.length) setSelFy(r.data[0]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (selMonth) api.get("/reports/period", { params: { month: selMonth } }).then((r) => setMonthStat(r.data)).catch(() => {});
  }, [selMonth]);
  useEffect(() => {
    if (selFy) api.get("/reports/period", { params: { fy: selFy } }).then((r) => setFyStat(r.data)).catch(() => {});
  }, [selFy]);

  const hasTrend = trend.some((t) => t.total > 0);
  const totalShareTons = shares.reduce((s, x) => s + x.tons, 0);

  return (
    <div>
      <h1 className="font-chivo font-black text-3xl text-slate-900">Dashboard</h1>
      <p className="text-slate-500 mt-1 mb-8">ST FRANCIS XAVIER ICE PLANT — operational overview</p>

      {!data ? (
        <p className="text-slate-400">Loading…</p>
      ) : (
        <div className="grid gap-5 md:grid-cols-3">
          <TonsCard title="Today" subtitle={fmtDate(data.today.date)} data={data.today} icon={CalendarDays} accent="bg-sky-500" />
          <TonsCard title="This Month" subtitle={monthLabel(data.month.month)} data={data.month} icon={CalendarRange} accent="bg-indigo-500" amount={data.month.sales_amount} />
          <TonsCard title="Financial Year" subtitle={`FY ${data.fy.fy}`} data={data.fy} icon={TrendingUp} accent="bg-emerald-500" amount={data.fy.sales_amount} />
        </div>
      )}

      {/* Sales comparison dropdowns */}
      <div className="mt-8 grid gap-5 md:grid-cols-2">
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="compare-month">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="p-2 rounded-md bg-slate-100 text-slate-600"><CalendarRange className="h-4 w-4" /></span>
              <h2 className="font-chivo font-bold text-slate-900">Monthly Sales</h2>
            </div>
            <div className="w-44">
              <Select value={selMonth} onValueChange={setSelMonth}>
                <SelectTrigger data-testid="compare-month-select"><SelectValue placeholder="Select month" /></SelectTrigger>
                <SelectContent>
                  {months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {months.length === 0 ? <p className="text-slate-400 text-sm mt-4">No sales yet.</p>
            : <CompareBox tons={monthStat?.total_tons} amount={monthStat?.sales_amount} />}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="compare-fy">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="p-2 rounded-md bg-slate-100 text-slate-600"><TrendingUp className="h-4 w-4" /></span>
              <h2 className="font-chivo font-bold text-slate-900">Financial Year Sales</h2>
            </div>
            <div className="w-44">
              <Select value={selFy} onValueChange={setSelFy}>
                <SelectTrigger data-testid="compare-fy-select"><SelectValue placeholder="Select FY" /></SelectTrigger>
                <SelectContent>
                  {fys.map((f) => <SelectItem key={f} value={f}>FY {f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {fys.length === 0 ? <p className="text-slate-400 text-sm mt-4">No sales yet.</p>
            : <CompareBox tons={fyStat?.total_tons} amount={fyStat?.sales_amount} />}
        </div>
      </div>

      {/* Trends + customer share */}
      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="dashboard-trends">
          <div className="flex items-center gap-2 mb-1">
            <span className="p-2 rounded-md bg-slate-100 text-slate-600"><BarChart3 className="h-4 w-4" /></span>
            <h2 className="font-chivo font-bold text-slate-900">Monthly Tons — last 12 months</h2>
          </div>
          <p className="text-sm text-slate-400 mb-5">Busy vs slow months at a glance (SFX 1 + SFX 2).</p>
          {!hasTrend ? (
            <p className="text-slate-400 text-sm py-10 text-center">No sales in the last 12 months yet.</p>
          ) : (
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={trend} margin={{ top: 8, right: 8, left: -12, bottom: 0 }} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="month" tickFormatter={monthShort} tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "#F1F5F9" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="sfx1" name="SFX 1" stackId="a" fill="#0EA5E9" />
                  <Bar dataKey="sfx2" name="SFX 2" stackId="a" fill="#4F46E5" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="customer-share">
          <div className="flex items-center gap-2 mb-1">
            <span className="p-2 rounded-md bg-slate-100 text-slate-600"><PieIcon className="h-4 w-4" /></span>
            <h2 className="font-chivo font-bold text-slate-900">Customer Share</h2>
          </div>
          <p className="text-sm text-slate-400 mb-3">Tons this month by customer</p>
          {totalShareTons === 0 ? (
            <p className="text-slate-400 text-sm py-10 text-center">No sales this month yet.</p>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={shares} dataKey="tons" nameKey="customer" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}>
                    {shares.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${fmtTons(v)} t (${Math.round((v / totalShareTons) * 100)}%)`, n]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
